<template>
  <div class="addingAppsContainer">
    <v-btn
      v-if="canSeePermission"
      @click="showModal = true"
      :disabled="isDisabled || loading"
      :color="buttonColor"
      rbt-target="openApps"
      large
    >
      <v-icon left>action_add_circle</v-icon>
      <span>{{ $t("WorkspaceEdit.addApps.buttonLabel") }}</span>
    </v-btn>

    <popup
      v-model="showModal"
      :title="$t('WorkspaceEdit.addApps.title')"
      :loading="loader.on"
      :prevent-close="loader.on"
      width="85vw"
      hide-footer
      no-padding
    >
      <apps-container v-if="showModal" :workspace="workspace" @add:apps="addApps" @cancel="cancel" />
    </popup>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, PropType } from "@vue/composition-api"
import { WorkspaceTemplate } from "@/types/workspace"
import { WorkspaceStates } from "@/config/workspace-states"
import { UsersModule } from "@/store/users"
import { WorkspaceModule } from "@/store"
import { AddedAssetsAndServices } from "./apps/utils/types"
import Popup from "@/components/popup/PopupRoot.vue"
import AppsContainer from "./apps/AppsContainer.vue"
import Utils from "@/utils/utils"
import Loader from "@/utils/loader"

export default defineComponent({
  name: "AddApps",
  props: {
    value: {
      type: Boolean,
      required: true
    },
    workspace: {
      type: Object as PropType<WorkspaceTemplate>,
      required: true
    },
    loading: {
      type: Boolean,
      default: true
    }
  },
  setup(props, ctx) {
    const showModal = Utils.vModel(props, ctx.emit)
    const usersModule = UsersModule()
    const spaceModule = WorkspaceModule()
    const loader = Loader()

    const isDisabled = computed(() => {
      return WorkspaceStates.isLoading(props.workspace.status) || WorkspaceStates.failed(props.workspace.status)
    })

    const buttonColor = computed(() => {
      const workspaceSchema = props.workspace.schema.assets
      const mandatoryAction = workspaceSchema.length === 0

      return mandatoryAction ? "primary" : "default"
    })

    const canSeePermission = computed(() => {
      return usersModule.hasEditorPermissions
    })

    const cancel = () => {
      showModal.value = false
    }

    const addApps = (added: AddedAssetsAndServices) => {
      loader.run(async () => {
        for (const app of added.assets) {
          await spaceModule.addApp(app)
        }

        for (const service of added.services) {
          await spaceModule.addService(service)
        }

        await Promise.all([
          spaceModule.getWorkspace(props.workspace.id),
          spaceModule.getAvailableApps(props.workspace.id),
          spaceModule.getAvailableServices(props.workspace.id)
        ])

        ctx.emit("add:apps-and-services")
        showModal.value = false
      })
    }

    return {
      loader,
      addApps,
      isDisabled,
      buttonColor,
      canSeePermission,
      showModal,
      cancel
    }
  },
  components: {
    AppsContainer,
    Popup
  }
})
</script>
